/**
 * GovernmentNoticesList — /government/notices
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bell, Loader2, Landmark, Clock } from "lucide-react";
import { http } from "@/lib/api";
import GovernmentCategoryNav from "@/components/GovernmentCategoryNav";

export default function GovernmentNoticesList() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const kind = params.get("kind") || "";
  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  useEffect(() => { http.get("/government/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const p = Object.fromEntries([...params.entries()]);
    http.get("/government/notices", { params: p })
      .then(r => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [params]);

  const noticeLabel = (s) => meta?.notice_kinds?.find(l => l.slug === s)?.label || s;

  return (
    <>
      <GovernmentCategoryNav />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="notices-list-page">
        <div className="overline mb-1">PUBLIC NOTICES</div>
        <h1 className="font-display text-4xl mb-1">Government notices</h1>
        <p className="opacity-70 mb-6">Announcements, tenders, road closures, health advisories and more.</p>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2">
          <select value={kind} onChange={e => setFilter("kind", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-kind">
            <option value="">All kinds</option>
            {meta?.notice_kinds?.map(k => <option key={k.slug} value={k.slug}>{k.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="opacity-60 inline-flex gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center opacity-70">No notices match.</div>
        ) : (
          <ul className="space-y-3">
            {items.map(n => (
              <li key={n.id} className="wofy-card !p-4" data-testid={`notice-${n.slug}`}>
                <div className="flex items-start gap-3">
                  <Bell className="w-4 h-4 opacity-70 mt-1" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">{noticeLabel(n.kind)}</div>
                    <div className="font-medium">{n.title}</div>
                    {n.summary && <p className="text-sm opacity-80 mt-1">{n.summary}</p>}
                    <div className="text-xs opacity-70 mt-2 flex flex-wrap gap-3">
                      <Link to={`/government/departments/${n.department_slug}`} className="inline-flex items-center gap-1 wofy-link">
                        <Landmark className="w-3 h-3" />{n.department_name}
                      </Link>
                      {n.valid_from && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />{n.valid_from}{n.valid_until ? ` – ${n.valid_until}` : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
